import React, { Fragment, Component } from 'react';
import {
    Grid,
    CircularProgress,
    Tooltip,
    Dialog,
    DialogContent,
    DialogActions,
    Button,
} from '@mui/material';
import { withStyles } from '@mui/styles';
import { withTranslation } from 'react-i18next';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import GetAppIcon from '@mui/icons-material/GetApp';
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import SvgIcon from '@mui/material/SvgIcon';
import moment from 'moment';
import { ReactComponent as AlamoIcon } from '/public/travelicons/caricons/Alamo.svg';
import { ReactComponent as AvisIcon } from '/public/travelicons/caricons/Avis.svg';
import { ReactComponent as BudgetIcon } from '/public/travelicons/caricons/Budget.svg';
import { ReactComponent as DollarIcon } from '/public/travelicons/caricons/Dollar.svg';
import { ReactComponent as EnterpriseIcon } from '/public/travelicons/caricons/Enterprise.svg';
import { ReactComponent as HertzIcon } from '/public/travelicons/caricons/Hertz.svg';
import { ReactComponent as NationalIcon } from '/public/travelicons/caricons/National.svg';
import { ReactComponent as GenericCarIcon } from '/public/travelicons/caricons/GenericCar.svg';
import { COLORS } from '../../../../utils/Application_Constants';
import { getTravelItinerary } from '../../../../services/AssignmentsService';

const styles = (theme) => ({
    AccordionSummaryRoot: {
        padding: '8px 20px',
    },
    title: {
        fontWeight: 'bold',
        fontSize: '16px',
        marginBottom: '4px',
        color: COLORS.RAVEN,
    },
    value: {
        fontSize: '14px',
        color: COLORS.RAVEN,
    },
    clickableLink: {
        fontWeight: '500',
        color: COLORS.TRUE_BLUE,
        fontSize: '14px',
        textDecoration: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
    },
    topRightIcon: {
        position: 'absolute',
        top: '8px',
        right: '16px',
    },
    carCard: {
        marginTop: '5px',
        backgroundColor: 'white',
        padding: '16px',
        minWidth: '100%',
        borderRadius: '8px',
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
        position: 'relative',
    },
    iconClass: {
        verticalAlign: 'middle',
        color: COLORS.TRUE_BLUE,
        fontSize: '36px',
    },
    buttonContainer: {
        display: 'flex',
        gap: '24px',
        marginTop: '8px',
        marginLeft: '16px',
        flexWrap: 'wrap',
    },
    buttonIcon: {
        marginRight: '8px',
        fontSize: '1.2rem',
        color: COLORS.TRUE_BLUE,
        backgroundColor: COLORS.LIGHT_GRAY,
        borderRadius: '50%',
        padding: '4px',
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
        verticalAlign: 'middle',
    },
    button: {
        background: 'none',
        border: 'none',
        fontWeight: '500',
        color: COLORS.TRUE_BLUE,
        fontSize: '14px',
        textDecoration: 'none',
        cursor: 'pointer',
        padding: '0',
        font: 'inherit',
        display: 'flex',
        alignItems: 'center',
        minWidth: 120,
        whiteSpace: 'nowrap',
    },
    disabledButton: {
        color: 'gray',
        fontWeight: '500',
        fontSize: '14px',
        cursor: 'not-allowed',
        textDecoration: 'none',
        pointerEvents: 'none',
    },
    buttonContent: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        flexDirection: 'row',
    },
    '@media (max-width: 600px)': {
        button: {
            minWidth: 'auto',
        },
        buttonContent: {
            flexDirection: 'row',
        },
        buttonContainer: {
            justifyContent: 'flex-start',
        },
    },
    section: {
        marginBottom: '12px',
    },
    column: {
        display: 'flex',
        flexDirection: 'column',
    },
    row: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    dialogPaperRoot: {
        padding: '40px',
        gap: '16px',
    },
    dialogContentRoot: {
        padding: '0',
        '&:first-child': {
            paddingTop: '0',
        },
    },
    dialogActionsRoot: {
        padding: '0',
    },
});

class CarRentalV3 extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isLoading: false,
            cancelModalOpen: false,
        };
    }

    renderIcon = (vendorName) => {
        const { classes } = this.props;
        switch (vendorName) {
            case 'Alamo':
                return (
                    <SvgIcon
                        aria-labelledby="alamoTitleID"
                        component={AlamoIcon}
                        className={classes.iconClass}
                        viewBox="0 0 38 38"
                    />
                );
            case 'Avis':
                return (
                    <SvgIcon
                        aria-labelledby="avisTitleID"
                        component={AvisIcon}
                        className={classes.iconClass}
                        viewBox="0 0 38 38"
                    />
                );
            case 'Budget':
                return (
                    <SvgIcon
                        aria-labelledby="budgetTitleID"
                        component={BudgetIcon}
                        className={classes.iconClass}
                        viewBox="0 0 38 38"
                    />
                );
            case 'Dollar':
                return (
                    <SvgIcon
                        aria-labelledby="dollarTitleID"
                        component={DollarIcon}
                        className={classes.iconClass}
                        viewBox="0 0 38 38"
                    />
                );
            case 'Enterprise':
                return (
                    <SvgIcon
                        aria-labelledby="enterpriseTitleID"
                        component={EnterpriseIcon}
                        className={classes.iconClass}
                        viewBox="0 0 38 38"
                    />
                );
            case 'Hertz':
                return (
                    <SvgIcon
                        aria-labelledby="hertzTitleID"
                        component={HertzIcon}
                        className={classes.iconClass}
                        viewBox="0 0 38 38"
                    />
                );
            case 'National':
                return (
                    <SvgIcon
                        aria-labelledby="nationalTitleID"
                        component={NationalIcon}
                        className={classes.iconClass}
                        viewBox="0 0 38 38"
                    />
                );
            default:
                return (
                    <SvgIcon
                        aria-labelledby="genericCarTitleID"
                        component={GenericCarIcon}
                        className={classes.iconClass}
                        viewBox="0 0 38 38"
                    />
                );
        }
    };

    renderErrorDialog() {
        const { cancelModalOpen } = this.state;
        const { classes } = this.props;

        return (
            <Dialog
                open={cancelModalOpen}
                onClose={() => this.setState({ cancelModalOpen: false })}
                aria-labelledby="error-modal-title"
                aria-describedby="error-modal-description"
                classes={{ paper: classes.dialogPaperRoot }}
                fullWidth
                maxWidth="xs"
            >
                <DialogContent
                    id="error-modal-description"
                    classes={{ root: classes.dialogContentRoot }}
                >
                    The travel document is not currently available. For immediate assistance, please
                    contact your Clinician Relations Specialist.
                </DialogContent>
                <DialogActions classes={{ root: classes.dialogActionsRoot }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => this.setState({ cancelModalOpen: false })}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    render() {
        const { classes, item, t, externalId, assignmentId } = this.props;
        const { isLoading } = this.state;
        const isItineraryAvailable = !!item.eItinLink;
        const isCompletedOver30Days =
            item.completedDate &&
            moment(item.completedDate).isBefore(moment().subtract(30, 'days'));

        return (
            <Fragment key={`${item.tripKey}-${item.confirmationNumber}`}>
                {this.renderErrorDialog()}
                <Grid container direction="column" className={classes.carCard}>
                    {/* Top Left: Flight Details */}
                    <div className={classes.row}>
                        <div className={classes.column}>
                            <div className={classes.section}>
                                <div className={classes.title}>CAR</div>
                                <div className={classes.value}>{`${item.vendorName}`}</div>
                            </div>
                        </div>
                        <div className={classes.topRightIcon}>
                            {this.renderIcon(item.vendorName)}
                        </div>
                    </div>
                    {/* Departure, Arrival, Confirmation */}
                    <div className={classes.row}>
                        <div className={classes.column}>
                            <div className={classes.title}>PICK UP</div>
                            <div className={classes.value}>{`${moment(item.originatedDate).format(
                                'MM/DD/YYYY'
                            )}`}</div>
                        </div>
                        <div className={classes.column}>
                            <div className={classes.title}>RETURN</div>
                            <div className={classes.value}>{`${moment(item.completedDate).format(
                                'MM/DD/YYYY'
                            )}`}</div>
                        </div>
                        <div className={classes.column}>
                            <div className={classes.title}>CONFIRMATION</div>
                            <div className={classes.value}>
                                <InsertDriveFileOutlinedIcon fontSize="small" />
                                {item.confirmationNumber}
                            </div>
                        </div>
                    </div>
                    <div className={classes.buttonContainer}>
                        {(() => {
                            if (!isCompletedOver30Days && isItineraryAvailable) {
                                return (
                                    <>
                                        <a
                                            href={item.eItinLink}
                                            className={classes.clickableLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <OpenInNewIcon className={classes.buttonIcon} />
                                            Itinerary
                                        </a>
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                this.setState({ isLoading: true });
                                                try {
                                                    await getTravelItinerary(
                                                        externalId,
                                                        assignmentId,
                                                        item.recordLocator,
                                                        moment(item.firstTravelDate).format(
                                                            'MM-DD-YYYY'
                                                        ),
                                                        moment(item.lastTravelDate).format(
                                                            'MM-DD-YYYY'
                                                        )
                                                    );
                                                } catch (error) {
                                                    console.error('Error downloading file:', error);
                                                    if (error && error.response.status === 404) {
                                                        this.setState({ cancelModalOpen: true });
                                                    } else {
                                                        alert(
                                                            'Failed to download the file. Please try again.'
                                                        );
                                                    }
                                                } finally {
                                                    this.setState({ isLoading: false });
                                                }
                                            }}
                                            className={`${classes.button} ${
                                                isLoading ? classes.disabledButton : ''
                                            }`}
                                            disabled={isLoading}
                                            aria-label="Download Itinerary"
                                        >
                                            <span className={classes.buttonContent}>
                                                {isLoading ? (
                                                    <CircularProgress
                                                        size={20}
                                                        className={classes.buttonIcon}
                                                    />
                                                ) : (
                                                    <GetAppIcon className={classes.buttonIcon} />
                                                )}
                                                Download
                                            </span>
                                        </button>
                                    </>
                                );
                            }

                            if (!isCompletedOver30Days) {
                                return (
                                    <>
                                        <Tooltip title="Booking information not available">
                                            <span>
                                                <button
                                                    type="button"
                                                    onClick={(e) => e.preventDefault()}
                                                    className={`${classes.clickableLink} ${classes.disabledButton}`}
                                                    disabled
                                                    aria-label="Itinerary unavailable"
                                                >
                                                    <OpenInNewIcon
                                                        className={`${classes.buttonIcon} ${classes.disabledButton}`}
                                                    />
                                                    Itinerary
                                                </button>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Travel information not available">
                                            <span className={classes.buttonContent}>
                                                <button
                                                    type="button"
                                                    className={`${classes.button} ${classes.disabledButton}`}
                                                    disabled
                                                    aria-label="Download Itinerary"
                                                >
                                                    <GetAppIcon
                                                        className={`${classes.buttonIcon} ${classes.disabledButton}`}
                                                    />
                                                    Download
                                                </button>
                                            </span>
                                        </Tooltip>
                                    </>
                                );
                            }

                            return null;
                        })()}
                    </div>
                </Grid>
            </Fragment>
        );
    }
}

export default withTranslation()(withStyles(styles)(CarRentalV3));
